
import Ajv from 'ajv'
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils';
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest';
import { AdvancedAnalysis } from '../models/AdvancedAnalysis';
import { AdvancedAnalysisSchema } from '../models/AdvancedAnalysisSchema';
import { AdvancedAnalysisService } from './AdvancedAnalysisService';
import { AdvancedAnalysisTypeGuardService } from './AdvancedAnalysisTypeGuardService';
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult';
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService';


export class AdvancedAnalysisJsonParsingResult extends CdnSchemaJsonParsingResult<AdvancedAnalysis, AdvancedAnalysisJsonParsingInputType> {

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            // Attempt to extract rules based on input shape
            if (Array.isArray(parsedValue)) {
                this.parsedEntities = parsedValue;
                this.inputType = 'array';
            }
            else {
                this.inputType = 'full';
            }
        }
    }

    public withParsedAdvancedAnalyses(rules: AdvancedAnalysis[]): this {
        this.parsedEntities = rules;
        return this;
    }
}


// Represents a potential input json file structure that is solely an array of rule
type IAdvancedAnalysesArray = AdvancedAnalysis[];


type AdvancedAnalysisJsonParsingInputType = 'full' | 'array';

export class AdvancedAnalysisJsonParsingService extends SchemaJsonValidationService<AdvancedAnalysis, AdvancedAnalysisJsonParsingInputType, AdvancedAnalysisSchema> {

    static readonly injectableKey = 'AdvancedAnalysisJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof AdvancedAnalysisJsonParsingService).injectableKey;
    }

    private _fullRulesPayloadStructureSchemaValidator = new Ajv().compile<IQueueExecutionRequest>({
        type: 'object',
        properties: {
            fileId: {
                type: 'string'
            },
            name: {
                type: 'string'
            },
            dataCleaning: {
                type: 'object',
                properties: {
                    rules: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    },
                    output: {
                        type: 'object',
                        properties: {
                            fileName: { type: 'string' }
                        },
                        required: ['fileName'],
                        additionalProperties: true
                    }
                },
                required: ['rules'],
                additionalProperties: true
            },
            calculation: {
                type: 'object',
                properties: {
                    type: { type: 'string' }
                },
                required: ['type'],
                additionalProperties: true
            },
            customCalculations: {
                type: 'object',
                properties: {
                    output: {
                        type: 'object',
                        properties: {
                            sort: {
                                type: 'array',
                                items: {
                                    type: 'string'
                                }
                            }
                        },
                        required: ['sort'],
                        additionalProperties: true
                    },
                    calculations: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['output', 'calculations'],
                additionalProperties: true
            },
            customPlots: {
                type: 'object',
                properties: {
                    plots: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['plots'],
                additionalProperties: true
            },
            customTables: {
                type: 'object',
                properties: {
                    tables: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['tables'],
                additionalProperties: true
            },
            customReport: {
                type: 'object',
                properties: {
                    reports: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['reports'],
                additionalProperties: true
            }
        },
        required: ['fileId', 'calculation'],
        additionalProperties: true
    });

    private _advancedAnalysesArraySchemaValidator = new Ajv().compile<IAdvancedAnalysesArray>({
        type: 'array',
        items: {
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        }
    });

    //@ts-ignore
    public initializeValidators(defaultType?: string): void {
        //noop
    }

    // Attempts to parse, validate, and initialize advanced analyses array that can operate with the UI
    // Returns validation result indicating parse and schema validation success, optionally on success
    // with UI-initializd advanced analyses array.
    public tryParseEntitiesFromJson(value: string, advancedAnalysesSchemas: Array<AdvancedAnalysisSchema>): AdvancedAnalysisJsonParsingResult {
        if (advancedAnalysesSchemas == null || advancedAnalysesSchemas.length <= 0) throw new Error("advancedAnalysesSchemas is required");

        var result = new AdvancedAnalysisJsonParsingResult(true);

        if (value == null) {
            result.valid = false;
            result.errors.push("Cannot parse JSON from null or empty input value.");
            return result;
        }
        try {
            result.parsedValue = JSON.parse(value);
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParseAdvancedAnalyses(result, advancedAnalysesSchemas);
        }
        catch (error: any) {
            result.valid = false;
            result.errors.push(error);
        }

        return result;

    }

    private tryParseAdvancedAnalyses(result: AdvancedAnalysisJsonParsingResult, advancedAnalysesSchemas: Array<AdvancedAnalysisSchema>): AdvancedAnalysisJsonParsingResult {

        if (result.parsedValue == null) throw new Error("Invalid advancedAnalyses.parseValue encountered trying to validateAdvancedAnalysesStructure.");

        const parsed = result.parsedValue;
        let advancedAnalyses: Omit<AdvancedAnalysis, '_type'>[] | undefined;

        // reset errors from any prior validation
        this._fullRulesPayloadStructureSchemaValidator.errors = null;
        // disable array parsing until we know we want to support this
        //this.advancedAnalysesArraySchemaValidator.errors = null;

        if (this._fullRulesPayloadStructureSchemaValidator(parsed)) {
            advancedAnalyses = [parsed]
        }
        //else if (this.advancedAnalysesArraySchemaValidator(parsed)) {
        //advancedAnalyses = parsed;
        //}
        else {
            const errors = [
                ...(this._fullRulesPayloadStructureSchemaValidator.errors || []),
                ...(this._advancedAnalysesArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new AdvancedAnalysisJsonParsingResult(false, undefined, errors);
        }

        try {
            const initialized = advancedAnalyses.map(x =>
                this.initializeEntityWithConfiguration(x as AdvancedAnalysis, advancedAnalysesSchemas)
            );
            return new AdvancedAnalysisJsonParsingResult(true, parsed).withParsedAdvancedAnalyses(initialized);
        } catch (err: any) {
            return new AdvancedAnalysisJsonParsingResult(false, parsed, [`${err.message}`]);
        }

    }

    // For lack of a better term, invert the flattening of a rule and instead populate its configuration
    // from the rule itself when loading rules into the UI
    public initializeEntityWithConfiguration(advancedAnalysis: AdvancedAnalysis, advancedAnalysesSchemas: Array<AdvancedAnalysisSchema>): AdvancedAnalysis {
        if (advancedAnalysis == null) throw new Error("advancedAnalysis is required");
        if (!advancedAnalysis.type) throw new Error("advancedAnalysis.type is required")
        return this.initializeEntity(advancedAnalysis, advancedAnalysesSchemas, 1);
    }

    public initializeEntity(advancedAnalysis: AdvancedAnalysis, advancedAnalysesSchemas: Array<AdvancedAnalysisSchema>, configurationLevel: number): AdvancedAnalysis {
        let initializedAdvancedAnalysis = advancedAnalysis;
        //Match the incoming type on case-sensitive known rules
        const advancedAnalysisSchema = AdvancedAnalysisService.tryGetAdvancedAnalysisSchemaByType(advancedAnalysis.type, advancedAnalysesSchemas);
        if (advancedAnalysisSchema != null) {
            initializedAdvancedAnalysis = AdvancedAnalysisService.createAdvancedAnalysisFromAdvancedAnalysisSchema(advancedAnalysisSchema);
            if (advancedAnalysis.description != null) initializedAdvancedAnalysis.description = advancedAnalysis.description;
            initializedAdvancedAnalysis._type = advancedAnalysis.type;
            initializedAdvancedAnalysis.configuration = destructureWithOmit(advancedAnalysis, []);
            initializedAdvancedAnalysis.configuration._type = advancedAnalysis.type;

            // Special-case for rule comprised of rules (E.g. Data Grouping) or RuleSchema with payload_template that may have groups 
            const advancedAnalysisRequiresRecursiveInitialization = this.advancedAnalysisRequiresRecursiveInitialization(advancedAnalysis);
            //TODruleRequiresRecursiveInitializationO: evaluate payload_template itself for groups instead of just disabling
            const ruleSchemaRequiresRecursiveInitialization = this.ruleSchemaRequiresRecursiveInitialization(advancedAnalysisSchema);

            if (configurationLevel > 1 &&
                (advancedAnalysisRequiresRecursiveInitialization || ruleSchemaRequiresRecursiveInitialization)) {
                throw new Error("Non top-level advanced analyses that contain advanced analyses not supported.");
            }
            else if (advancedAnalysisRequiresRecursiveInitialization) {
                const initializedSubAdvancedAnalyses = new Array<AdvancedAnalysis>();
                (initializedAdvancedAnalysis.configuration?.advancedAnalyses || []).forEach((aa: AdvancedAnalysis) => {
                    initializedSubAdvancedAnalyses.push(this.initializeEntity(aa, advancedAnalysesSchemas, configurationLevel + 1))
                });
                initializedAdvancedAnalysis.configuration.advancedAnalyses = initializedSubAdvancedAnalyses;
            }
        }
        return initializedAdvancedAnalysis;
    }

    private advancedAnalysisRequiresRecursiveInitialization(advancedAnalysis: AdvancedAnalysis): boolean {
        return AdvancedAnalysisTypeGuardService.supportsChildAdvancedAnalyses(advancedAnalysis);
    }

    private ruleSchemaRequiresRecursiveInitialization(advancedAnalysisSchema: AdvancedAnalysisSchema): boolean {
        return AdvancedAnalysisTypeGuardService.supportsChildAdvancedAnalyses(advancedAnalysisSchema);
    }



}